import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, Trash2, Save, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import MarginWarningDialog from './MarginWarningDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const peso = (n) => `Php ${(Number(n) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ConstructionProposalDialog({ projectId, open, onOpenChange, materialsMargin, laborMargin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [warnOpen, setWarnOpen] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/api/proposal/${projectId}`); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load proposal'); onOpenChange(false); }
    finally { setLoading(false); }
  }, [projectId, onOpenChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const set = (patch) => setData(d => ({ ...d, ...patch }));
  const subtotal = Number(data?.subtotal) || 0;
  const vat = data?.with_vat ? +(subtotal * 0.12).toFixed(2) : 0;
  const total = +(subtotal + vat).toFixed(2);

  const setTerm = (i, patch) => setData(d => ({ ...d, terms: d.terms.map((t, idx) => idx === i ? { ...t, ...patch } : t) }));
  const addTerm = () => setData(d => ({ ...d, terms: [...d.terms, { percent: 0, description: '' }] }));
  const delTerm = (i) => setData(d => ({ ...d, terms: d.terms.filter((_, idx) => idx !== i) }));

  const payload = () => ({
    date_created: data.date_created,
    re_line: data.re_line, intro_line: data.intro_line, with_vat: data.with_vat,
    terms: data.terms.map(t => ({ percent: Number(t.percent) || 0, description: t.description })),
    timeline_text: data.timeline_text, excluded_works: data.excluded_works,
  });

  const save = async () => {
    setSaving(true);
    try { const r = await axios.post(`${API_URL}/api/proposal/${projectId}`, payload()); setData(r.data); toast.success('Proposal saved'); return true; }
    catch { toast.error('Failed to save proposal'); return false; }
    finally { setSaving(false); }
  };

  const requestDownload = () => {
    const zero = (Number(materialsMargin) || 0) === 0 || (Number(laborMargin) || 0) === 0;
    if (zero) setWarnOpen(true);
    else downloadPdf();
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await save();
      const r = await axios.get(`${API_URL}/api/proposal/${projectId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Construction_Proposal_${(data.project_name || 'project').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to generate PDF'); }
    finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="proposal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /> Construction Proposal</DialogTitle>
          <DialogDescription>Auto-filled from the project BOM. Edit the fields, save, and download as PDF.</DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Loading proposal...</p>
        ) : (
          <div className="space-y-5 text-sm">
            <div className="flex items-center gap-2 border-b pb-3">
              <div className="text-2xl font-extrabold tracking-tight">desq</div>
              <div className="text-muted-foreground text-xs">Construction</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Date</Label>
                <Input type="date" value={data.date_created} onChange={e => set({ date_created: e.target.value })} data-testid="proposal-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">To (Client)</Label>
                <div className="h-9 flex items-center px-3 rounded-md border bg-muted/50 text-sm" data-testid="proposal-to">{data.to_name || <span className="text-muted-foreground">Not set in Project Directory</span>}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Re</Label>
                <Input value={data.re_line} onChange={e => set({ re_line: e.target.value })} data-testid="proposal-re" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Location</Label>
                <div className="h-9 flex items-center px-3 rounded-md border bg-muted/50 text-sm" data-testid="proposal-location">{data.location || <span className="text-muted-foreground">Not set in Project Directory</span>}</div>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-semibold">Proposal title (editable)</Label>
              <Input value={data.intro_line} onChange={e => set({ intro_line: e.target.value })} data-testid="proposal-intro" />
            </div>

            <div className="rounded-md bg-muted/50 p-3 text-xs">
              We are pleased to submit our <b>{data.intro_line}</b> for the amount of:{' '}
              <b data-testid="proposal-amount-words">{data.total_in_words}</b> (<b>{peso(total)}</b>), as a Lump sum Cost for labor and Materials.
            </div>

            {/* Cost Summary Breakdown */}
            <div>
              <div className="font-bold mb-1">I. Cost Summary Breakdown</div>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <tbody>
                    {data.cost_summary.map((c) => (
                      <tr key={c.letter} className="border-b" data-testid={`proposal-cat-${c.letter}`}>
                        <td className="p-2">{c.letter}. {c.name}</td>
                        <td className="p-2 text-right w-40">{peso(c.amount)}</td>
                      </tr>
                    ))}
                    <tr className="border-b"><td className="p-2 text-right font-semibold">Sub Total</td><td className="p-2 text-right" data-testid="proposal-subtotal">{peso(subtotal)}</td></tr>
                    <tr className="border-b">
                      <td className="p-2 text-right">
                        <label className="inline-flex items-center gap-2 justify-end w-full cursor-pointer">
                          <input type="checkbox" checked={data.with_vat} onChange={e => set({ with_vat: e.target.checked })} data-testid="proposal-vat-checkbox" />
                          <span>VAT 12% {data.with_vat ? '(applied)' : '(no VAT)'}</span>
                        </label>
                      </td>
                      <td className="p-2 text-right" data-testid="proposal-vat">{peso(vat)}</td>
                    </tr>
                    <tr className="bg-slate-800 text-white font-bold"><td className="p-2 text-right">TOTAL CONSTRUCTION COST</td><td className="p-2 text-right" data-testid="proposal-total">{peso(total)}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Terms of Payment */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="font-bold">II. Terms of Payment</div>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-primary" onClick={addTerm} data-testid="proposal-add-term"><Plus className="w-3 h-3 mr-1" /> Add row</Button>
              </div>
              <p className="text-[11px] text-muted-foreground mb-1.5">These payment terms and the total contract price automatically mirror into the project's Billing Monitoring (Construction phases &amp; contract amount) when you save.</p>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted"><tr><th className="p-2 w-16">%</th><th className="p-2 text-left">Description</th><th className="p-2 text-right w-36">Amount</th><th className="w-10"></th></tr></thead>
                  <tbody>
                    {data.terms.map((t, i) => (
                      <tr key={i} className="border-t" data-testid={`proposal-term-${i}`}>
                        <td className="p-1"><Input type="number" value={t.percent} onChange={e => setTerm(i, { percent: e.target.value === '' ? '' : parseFloat(e.target.value) })} className="h-7 text-xs text-center" data-testid={`proposal-term-percent-${i}`} /></td>
                        <td className="p-1"><Input value={t.description} onChange={e => setTerm(i, { description: e.target.value })} className="h-7 text-xs" data-testid={`proposal-term-desc-${i}`} /></td>
                        <td className="p-2 text-right">{peso(total * (Number(t.percent) || 0) / 100)}</td>
                        <td className="p-1 text-center"><button onClick={() => delTerm(i)} className="text-red-400 hover:text-red-600" data-testid={`proposal-del-term-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-semibold">IV. Project Timeline</Label>
              <Input value={data.timeline_text} onChange={e => set({ timeline_text: e.target.value })} data-testid="proposal-timeline" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold">V. Excluded Works</Label>
              <Textarea value={data.excluded_works} onChange={e => set({ excluded_works: e.target.value })} rows={3} placeholder="List any excluded works..." data-testid="proposal-excluded" />
            </div>

            <div className="flex items-center justify-end gap-2 border-t pt-4 sticky bottom-0 bg-background">
              <Button variant="outline" onClick={save} disabled={saving} data-testid="proposal-save-btn"><Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}</Button>
              <Button onClick={requestDownload} disabled={downloading} data-testid="proposal-download-btn"><Download className="w-4 h-4 mr-1" /> {downloading ? 'Preparing...' : 'Download PDF'}</Button>
            </div>
          </div>
        )}
      </DialogContent>
      <MarginWarningDialog open={warnOpen} onCancel={() => setWarnOpen(false)} onConfirm={() => { setWarnOpen(false); downloadPdf(); }} materialsMargin={materialsMargin} laborMargin={laborMargin} context="the proposal" />
    </Dialog>
  );
}
